import { Box, ButtonBase, Stack, Typography } from "@mui/material"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import type { Locker } from "shared/locker"
import { formatTemperatureValue } from "./temperatureDemoFormat"
import { temperatureAt } from "./temperatureDemoGenerator"
import { SCENARIO_LOOK } from "./temperatureDemoScenarios"
import { TemperatureDemoDoor, temperatureDemoDoorList, temperatureDemoModules } from "./temperatureDemoDoors"
import type { TemperatureDemoScenario } from "./temperatureDemoTypes"

/** Mini map scale vs raw SST cell units (the dashboard uses 0.6). */
const MINI_RATIO = 0.26
/** Below this height a mini tile shows only its label. */
const MIN_TEMP_HEIGHT = 36

type Props = {
  locker: Locker
  doors: Record<string, TemperatureDemoScenario>
  selectedDoorId: string | undefined
  onPick: (doorId: string) => void
  now: Date
}

type MiniDoorProps = {
  door: TemperatureDemoDoor
  scenario: TemperatureDemoScenario | undefined
  selected: boolean
  onPick: (doorId: string) => void
  now: Date
  showTemp: boolean
}

const MiniDoor: FC<MiniDoorProps> = ({ door, scenario, selected, onPick, now, showTemp }) => {
  const { t, i18n } = useTranslation()
  const look = SCENARIO_LOOK[scenario ?? "off"]
  return (
    <ButtonBase
      aria-pressed={selected}
      aria-label={t("temperatureDemo.miniDoorAria", {
        door: door.label,
        scenario: t(look.labelKey),
      })}
      onClick={() => onPick(door.id)}
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: 2,
        boxSizing: "border-box",
        bgcolor: look.bg,
        border: look.border,
        color: look.fg,
        display: "flex",
        flexDirection: "column",
        gap: "1px",
        fontSize: 11,
        fontWeight: 700,
        fontFamily: "inherit",
        ...(selected && { outline: "3px solid #234285", outlineOffset: "2px" }),
        "&.Mui-focusVisible": { outline: "3px solid #234285", outlineOffset: "2px" },
      }}
    >
      <span>{door.label}</span>
      {scenario && showTemp && (
        <Box component="span" sx={{ fontSize: 9.5, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {`${formatTemperatureValue(temperatureAt(door.id, scenario, 0, now), i18n.language)}°`}
        </Box>
      )}
    </ButtonBase>
  )
}

/**
 * LOCKER-534 — the locker drawn small, one button per door, so a tenant user
 * picks a door by where it is. Same module order and cell geometry as the
 * dashboard (`temperatureDemoModules`: V2 templates or V1 `locker_grid`).
 */
export const TemperatureDemoMiniMap: FC<Props> = ({ locker, doors, selectedDoorId, onPick, now }) => {
  const { t } = useTranslation()
  // LOCKER-535 — V1 (`sstLocker`, virtual included) or V2 (`sst2Locker`).
  const allDoors = useMemo(() => temperatureDemoDoorList(locker), [locker])
  const modules = useMemo(() => temperatureDemoModules(locker, allDoors), [locker, allDoors])

  const renderDoor = (door: TemperatureDemoDoor, showTemp: boolean) => (
    <MiniDoor
      door={door}
      scenario={doors[door.id]}
      selected={selectedDoorId === door.id}
      onPick={onPick}
      now={now}
      showTemp={showTemp}
    />
  )

  if (modules.length === 0) {
    // No template geometry: a plain wrap of doors, in door order.
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {allDoors.map((d) => (
          <Box key={d.id} sx={{ width: 56, height: 44 }}>
            {renderDoor(d, true)}
          </Box>
        ))}
      </Stack>
    )
  }

  return (
    <Stack direction="row" flexWrap="wrap" gap={1.5}>
      {modules.map((mod, i) => {
        const width = mod.width * MINI_RATIO
        const height = mod.height * MINI_RATIO
        return (
          <Stack
            key={mod.key}
            spacing={0.75}
            sx={{ bgcolor: "#f5f5f5", border: "3px solid #e0e0e0", borderRadius: 4, p: "8px 10px 10px" }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>
              {`${t("module")} ${String.fromCharCode(65 + i)}`}
            </Typography>
            <Box sx={{ position: "relative", width, height }}>
              {mod.cells.map((cell) => {
                const box = {
                  position: "absolute" as const,
                  top: cell.y * MINI_RATIO,
                  left: cell.x * MINI_RATIO,
                  width: cell.width * MINI_RATIO - 4,
                  height: cell.height * MINI_RATIO - 4,
                }
                if (!cell.isDoor) {
                  return (
                    <Box key={`c-${cell.key}`} aria-hidden sx={{ ...box, bgcolor: "#e0e0e0", borderRadius: 2 }} />
                  )
                }
                const door = cell.door
                if (!door) return null
                return (
                  <Box key={door.id} sx={box}>
                    {renderDoor(door, box.height >= MIN_TEMP_HEIGHT)}
                  </Box>
                )
              })}
            </Box>
          </Stack>
        )
      })}
    </Stack>
  )
}
